import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SpeechMark } from './TtsContext'
import type { PresignedUrlResponse } from './types'

const parseSpeechMarks = (raw: string): SpeechMark[] => {
    if (!raw) return []
    let marks: SpeechMark[] = []
    try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) marks = parsed
    } catch {
        marks = raw
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => {
                try {
                    return JSON.parse(line)
                } catch {
                    return null
                }
            })
            .filter((item) => item !== null) as SpeechMark[]
    }
    return marks
        .filter(
            (mark): mark is SpeechMark =>
                mark !== null && typeof mark.type === 'string',
        )
        .sort((a, b) => (a.time ?? 0) - (b.time ?? 0))
}

const findCurrentWordIndex = (marks: SpeechMark[], timeMs: number): number => {
    if (marks.length === 0) return 0
    let low = 0,
        high = marks.length - 1,
        result = 0
    while (low <= high) {
        const mid = Math.floor((low + high) / 2)
        if (marks[mid].time <= timeMs) {
            result = mid
            low = mid + 1
        } else {
            high = mid - 1
        }
    }
    return result
}

export interface UseTtsPlayerStateOptions {
    /** When true, the player (and audio element) is mounted; use so the hook can attach listeners after expand. */
    isPlayerExpanded?: boolean
}

export function useTtsPlayerState(
    presignedUrls: PresignedUrlResponse,
    options: UseTtsPlayerStateOptions = {},
) {
    const { isPlayerExpanded = false } = options
    const [status, setStatus] = useState<
        'idle' | 'loading' | 'ready' | 'error'
    >('idle')
    const [urls, setUrls] = useState<PresignedUrlResponse>(presignedUrls)
    const [speechMarks, setSpeechMarks] = useState<SpeechMark[]>([])
    const [speechMarksLoadError, setSpeechMarksLoadError] = useState<
        string | null
    >(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [playbackRate, setPlaybackRateState] = useState(1)
    const audioRef = useRef<HTMLAudioElement>(null)
    const rafRef = useRef<number | null>(null)
    const isPlayingRef = useRef(false)
    const lastIndexRef = useRef(0)
    const lastAudioTimeRef = useRef(0)

    // Depend on primitive URL values only so we don't re-run when presignedUrls
    // is recreated with the same URLs (e.g. new object from parent re-render).
    // That would otherwise cause setUrls/setStatus → re-render → new presignedUrls
    // reference → effect again → infinite loop. We intentionally react only when
    // the actual URL strings change, not when the container object reference changes.
    const audioUrl = presignedUrls?.audioUrl
    const speechMarksUrl = presignedUrls?.speechMarksUrl

    useEffect(() => {
        if (audioUrl && speechMarksUrl) {
            setUrls({ audioUrl, speechMarksUrl })
            setStatus('loading')
        } else {
            setUrls({})
            setStatus('error')
        }
    }, [audioUrl, speechMarksUrl])

    useEffect(() => {
        // Early return if no speech marks URL - prevents any fetch operations
        if (!urls.speechMarksUrl || typeof window === 'undefined') {
            return
        }
        let active = true
        setStatus('loading')
        setSpeechMarksLoadError(null)
        fetch(urls.speechMarksUrl)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Speech marks failed: ${res.status}`)
                }
                return res.text()
            })
            .then((text) => {
                if (!active) return
                setSpeechMarksLoadError(null)
                const marks = parseSpeechMarks(text)
                setSpeechMarks(marks)
                setCurrentIndex(0)
                // Reset refs on new speech marks
                lastIndexRef.current = 0
                lastAudioTimeRef.current = 0
                setStatus('ready')
            })
            .catch((err) => {
                if (active) {
                    const message =
                        err instanceof Error ? err.message : String(err)
                    setSpeechMarksLoadError(message)
                    setSpeechMarks([])
                    setStatus('error')
                    console.warn(
                        '[TTS] Speech marks failed to load:',
                        message,
                        err,
                    )
                }
            })
        return () => {
            active = false
        }
    }, [urls.speechMarksUrl])

    const words = useMemo(() => {
        return speechMarks
            .filter((m) => m.type === 'word' && typeof m.time === 'number')
            .sort((a, b) => a.time - b.time)
    }, [speechMarks])

    const computeAndSetIndex = useCallback(() => {
        const audio = audioRef.current
        if (!audio || words.length === 0) return

        const timeMs = audio.currentTime * 1000
        const lastIdx = lastIndexRef.current
        const lastAudioTime = lastAudioTimeRef.current
        const isTimeGoingBackwards = timeMs + 50 < lastAudioTime

        const holdBackMs = 40
        const visualTime = Math.max(0, timeMs - holdBackMs)
        const candidate = findCurrentWordIndex(words, visualTime)

        // Monotonic progression during normal playback to avoid flicker
        let nextIdx = candidate
        if (!isTimeGoingBackwards) {
            if (nextIdx < lastIdx) nextIdx = lastIdx
            if (nextIdx > lastIdx + 1) nextIdx = lastIdx + 1
        }

        lastIndexRef.current = nextIdx
        lastAudioTimeRef.current = timeMs
        setCurrentIndex(nextIdx)
    }, [words])

    const startRafLoop = useCallback(() => {
        if (typeof window === 'undefined') return
        if (rafRef.current !== null) return

        const tick = () => {
            rafRef.current = null
            if (!isPlayingRef.current) return
            computeAndSetIndex()
            rafRef.current = window.requestAnimationFrame(tick)
        }

        rafRef.current = window.requestAnimationFrame(tick)
    }, [computeAndSetIndex])

    // Re-run when isPlayerExpanded becomes true so we attach listeners after the audio element is mounted
    useEffect(() => {
        const audio = audioRef.current
        if (!audio || words.length === 0) return
        const handlePlay = () => {
            isPlayingRef.current = true
            setIsPlaying(true)
            startRafLoop()
        }
        const handlePause = () => {
            isPlayingRef.current = false
            setIsPlaying(false)
        }
        const handleEnded = () => {
            isPlayingRef.current = false
            setIsPlaying(false)
        }
        audio.addEventListener('play', handlePlay)
        audio.addEventListener('pause', handlePause)
        audio.addEventListener('ended', handleEnded)
        const handleSeeked = () => {
            // On seek we allow index to move freely (including backwards).
            const timeMs = audio.currentTime * 1000
            lastAudioTimeRef.current = timeMs
            const candidate = findCurrentWordIndex(words, timeMs)
            lastIndexRef.current = candidate
            setCurrentIndex(candidate)
        }
        audio.addEventListener('seeked', handleSeeked)
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
        const handleDurationChange = () =>
            setDuration(Number.isFinite(audio.duration) ? audio.duration : 0)
        const handleLoadedMetadata = () =>
            setDuration(Number.isFinite(audio.duration) ? audio.duration : 0)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('durationchange', handleDurationChange)
        audio.addEventListener('loadedmetadata', handleLoadedMetadata)
        return () => {
            audio.removeEventListener('play', handlePlay)
            audio.removeEventListener('pause', handlePause)
            audio.removeEventListener('ended', handleEnded)
            audio.removeEventListener('seeked', handleSeeked)
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('durationchange', handleDurationChange)
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
        }
    }, [words, computeAndSetIndex, startRafLoop, isPlayerExpanded])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return
        audio.playbackRate = playbackRate
    }, [playbackRate, isPlayerExpanded])

    const setPlaybackRate = useCallback((rate: number) => {
        setPlaybackRateState(rate)
    }, [])

    const seekTo = useCallback(
        (timeSeconds: number) => {
            const audio = audioRef.current
            if (!audio) return
            const t = Math.max(0, Math.min(timeSeconds, duration || 0))
            audio.currentTime = t
            setCurrentTime(t)
        },
        [duration],
    )

    const stopAndReset = useCallback(() => {
        const audio = audioRef.current
        if (audio) {
            audio.pause()
            audio.currentTime = 0
        }
        isPlayingRef.current = false
        lastIndexRef.current = 0
        lastAudioTimeRef.current = 0
        setIsPlaying(false)
        setCurrentIndex(0)
        setCurrentTime(0)
        setDuration(0)
        if (rafRef.current !== null && typeof window !== 'undefined') {
            window.cancelAnimationFrame(rafRef.current)
            rafRef.current = null
        }
    }, [])

    return {
        status,
        urls,
        allSpeechMarks: speechMarks,
        speechMarksLoadError,
        currentWordIndex: currentIndex,
        isPlaying,
        audioRef,
        currentTime,
        duration,
        playbackRate,
        setPlaybackRate,
        seekTo,
        stopAndReset,
    }
}
